import type { BulkMediaApi } from './api'

/**
 * Staging (ADR 0025): the browser sends each file to the console once, in the
 * chunks the backend asks for (32 MiB; Cloudflare caps a request at 100 MB), a few
 * in parallel. The jobs process then uploads it to every Account.
 */

export const MAX_FILE_BYTES = 2 * 1024 ** 3
export const MAX_FILES = 50
export const MAX_ACCOUNTS = 30
export const MAX_CATEGORIES = 20

const EXT_TYPES: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  gif: 'image/gif',
  heic: 'image/heic',
  mp4: 'video/mp4',
  m4v: 'video/x-m4v',
  mov: 'video/quicktime',
  webm: 'video/webm',
  avi: 'video/x-msvideo',
  mkv: 'video/x-matroska',
}

/** The file's media type, from the browser or (when it has none) the extension. */
export function contentTypeOf(file: File): string {
  if (file.type) return file.type
  const ext = file.name.split('.').pop()?.toLowerCase() ?? ''
  return EXT_TYPES[ext] ?? ''
}

export function isPhotoOrVideo(file: File): boolean {
  const type = contentTypeOf(file)
  return type.startsWith('image/') || type.startsWith('video/')
}

export const ACCEPT = 'image/*,video/*'

export interface StageOptions {
  onProgress?: (fraction: number) => void
  signal?: AbortSignal
  /** Chunks in flight at once. */
  parallel?: number
  /** Attempts per chunk before the file fails. */
  tries?: number
  wait?: (ms: number) => Promise<void>
}

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms))

function aborted(signal?: AbortSignal) {
  return signal?.aborted || false
}

/** Send one file to the console; resolves with its staged file id. */
export async function stageFile(api: BulkMediaApi, file: File, opts: StageOptions = {}): Promise<string> {
  const { onProgress, signal, parallel = 3, tries = 3, wait = sleep } = opts
  if (file.size > MAX_FILE_BYTES) throw new Error('Files are limited to 2 GB')
  const staged = await api.createFile({ filename: file.name, content_type: contentTypeOf(file), size: file.size })
  const sent = new Array<number>(staged.chunks).fill(0)
  const report = () => onProgress?.(Math.min(1, sent.reduce((a, b) => a + b, 0) / file.size))
  report()

  let next = 0
  const worker = async () => {
    while (next < staged.chunks) {
      const index = next++
      const blob = file.slice(index * staged.chunk_size, (index + 1) * staged.chunk_size)
      for (let attempt = 1; ; attempt++) {
        if (aborted(signal)) throw new DOMException('Aborted', 'AbortError')
        try {
          await api.putChunk(staged.file_id, index, blob, (n) => {
            sent[index] = n
            report()
          }, signal)
          sent[index] = blob.size
          report()
          break
        } catch (e) {
          sent[index] = 0
          report()
          if (aborted(signal) || attempt >= tries) throw e
          await wait(1000 * 2 ** (attempt - 1))
        }
      }
    }
  }
  await Promise.all(Array.from({ length: Math.min(parallel, staged.chunks) }, worker))
  await api.completeFile(staged.file_id)
  onProgress?.(1)
  return staged.file_id
}

/** Run `fn` over `items`, at most `n` at a time; never rejects (each item handles its own errors). */
export async function runPool<T>(items: T[], n: number, fn: (item: T) => Promise<void>): Promise<void> {
  let next = 0
  const worker = async () => {
    while (next < items.length) {
      const item = items[next++] as T
      try {
        await fn(item)
      } catch {
        // the item reported it
      }
    }
  }
  await Promise.all(Array.from({ length: Math.min(n, items.length) }, worker))
}

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`
  if (n < 1024 ** 2) return `${(n / 1024).toFixed(0)} KB`
  if (n < 1024 ** 3) return `${(n / 1024 ** 2).toFixed(1)} MB`
  return `${(n / 1024 ** 3).toFixed(2)} GB`
}
